import { observer, useLocalObservable } from 'mobx-react-lite'
import React, { useState } from 'react'
import { nanoid } from 'nanoid'
import { Button, type ITabItem, Tabs } from 'shared/ui'
import { DefaultModalActions } from 'shared/ui/Modal/ui/DefaultModal/DefaultModalActions.tsx'
import { useEntitiesContext } from 'entities/EntitiesExport'
import type { ICallingActionPayload } from 'entities/AICalling/api/types.ts'
import { AgentVoiceResponse } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentVoiceResponse/AgentVoiceResponse.tsx'
import { AgentSMSMessage } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentSMSMessage/AgentSMSMessage.tsx'
import { AgentSMSMessageStore } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentSMSMessage/store/AgentSMSMessageStore.tsx'
import type { AgentInstructionsStore } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/store/AgentInstructionsStore.tsx'
import styles from './styles.module.scss'

export type ISendSMSContentProps = {
  onClose: () => void
  onSave: AgentInstructionsStore['onAddSMSAction']
  action?: ICallingActionPayload
  onDelete?: () => void
}

export const SendSMSContent = observer(
  ({ onClose, onSave, action, onDelete }: ISendSMSContentProps) => {
    const [token_id] = useState(action?.settings.token_id || `sms_${nanoid()}`)
    const [activeTabKey, setActiveTabKey] = useState<'during_call' | 'end_of_call'>(
      action?.trigger_type || 'during_call'
    )

    const entities = useEntitiesContext()

    const [agentVoiceResponseValue, setAgentVoiceResponseValue] = useState(
      action?.trigger_type === 'during_call' ? action.settings.voice_response : ''
    )

    const agentDuringCallSMSStore = useLocalObservable(
      () =>
        new AgentSMSMessageStore(
          entities,
          action?.trigger_type === 'during_call' ? action.settings : undefined
        )
    )
    const agentAfterCallSMSStore = useLocalObservable(
      () =>
        new AgentSMSMessageStore(
          entities,
          action?.trigger_type === 'end_of_call' ? action.settings : undefined
        )
    )

    const tabs: ITabItem<typeof activeTabKey>[] = [
      { key: 'during_call', name: 'During call' },
      {
        key: 'end_of_call',
        name: 'After call',
      },
    ]

    const handleSave = () => {
      if (activeTabKey === 'during_call') {
        const valid = agentDuringCallSMSStore.validate()
        if (!valid) return
        onSave({
          trigger_type: 'during_call',
          action_type: 'send_message',
          settings: {
            ...agentDuringCallSMSStore.params,
            token_id,
            voice_response: agentVoiceResponseValue,
          },
        })
      }
      if (activeTabKey === 'end_of_call') {
        const valid = agentAfterCallSMSStore.validate()
        if (!valid) return
        onSave({
          trigger_type: 'end_of_call',
          action_type: 'send_message',
          settings: {
            token_id,
            ...agentAfterCallSMSStore.params,
          },
        })
      }

      onClose()
    }

    const getDisabledSave = () => {
      if (activeTabKey === 'during_call') {
        return agentDuringCallSMSStore.disabled
      }
      if (activeTabKey === 'end_of_call') {
        return agentAfterCallSMSStore.disabled
      }
      return false
    }

    const handleDelete = () => {
      onDelete?.()
      onClose()
    }

    return (
      <>
        <div className={styles.content}>
          <Tabs
            activeTabKey={activeTabKey}
            handleSelectTab={(tab) => setActiveTabKey(tab.key)}
            tabs={tabs}
            marginInlineTab={-6}
          />
          {activeTabKey === 'during_call' && (
            <>
              <AgentVoiceResponse
                value={agentVoiceResponseValue}
                onChange={setAgentVoiceResponseValue}
              />
              <AgentSMSMessage store={agentDuringCallSMSStore} label={'SMS message'} />
            </>
          )}
          {activeTabKey === 'end_of_call' && (
            <>
              <AgentSMSMessage store={agentAfterCallSMSStore} />
            </>
          )}
        </div>
        <DefaultModalActions>
          {onDelete && (
            <Button
              style={{ marginRight: 'auto' }}
              typeBtn={'contained'}
              text={'Delete action'}
              size={'medium'}
              contained={'secondary'}
              intent={'destructive'}
              onClick={handleDelete}
            />
          )}
          <Button
            typeBtn={'contained'}
            text={'Cancel'}
            size={'medium'}
            contained={'secondary'}
            onClick={onClose}
          />
          <Button
            typeBtn={'contained'}
            text={'Save'}
            size={'medium'}
            contained={'primary'}
            onClick={handleSave}
            disabled={getDisabledSave()}
          />
        </DefaultModalActions>
      </>
    )
  }
)
